import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { List, makeStyles, Tab, Tabs, Typography } from '@material-ui/core';
import { FormattedMessage, useIntl } from 'react-intl';
import { Skeleton } from '@material-ui/lab';
import { MORE_AVAILABLE_OPTION_ID } from '~/modules/common/components/SearchAutocomplete/SearchAutocomplete';
import useRoles from '~/modules/common/hooks/useRoles';
import { SearchField } from '~/modules/common/components';
import RoleSelectionRow from './RoleSelectionRow';
import useSpecificRolesSelectorHandler from './useSpecificRolesSelectorHandler';

const TABS = { SELECTED: 'selected', ALL: 'all' };

const useStyles = makeStyles(theme => ({
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(1)
  },
  tab: {
    minWidth: 0,
    flex: 1,
    fontSize: theme.typography.caption.fontSize
  },
  tabCount: {
    fontWeight: theme.typography.fontWeightBold
  },
  tabLabel: {
    color: theme.palette.text.secondary
  },
  list: {
    maxHeight: 240,
    overflowY: 'auto'
  },
  emptyText: {
    ...theme.typography.body2,
    color: theme.palette.text.secondary,
    padding: theme.spacing(1, 0)
  },
  moreHint: {
    ...theme.typography.caption,
    color: theme.palette.text.secondary,
    textAlign: 'center',
    display: 'block',
    padding: theme.spacing(0.5, 0)
  }
}));

const SpecificRolesSelector = ({ selectedRoles, onSelectedRolesChange }) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const [activeTab, setActiveTab] = useState(TABS.ALL);

  const handleTabChange = useCallback((_, newTab) => setActiveTab(newTab), []);

  const [searchTerm, setSearchTerm] = useState('');
  const handleSearchChange = useCallback(
    e => setSearchTerm(e.target.value),
    []
  );

  const { hasMore, isLoading, defaultOptions: allRoles } = useRoles({
    searchTerm,
    pageSize: 20
  });

  const rolesForAllTab = useMemo(
    () => allRoles.filter(r => r.id && r.id !== MORE_AVAILABLE_OPTION_ID),
    [allRoles]
  );

  const allTabCount = `${rolesForAllTab.length}${hasMore ? '+' : ''}`;

  const {
    selectedRoleIds,
    defaultRoleUri,
    toggleRole,
    setDefault
  } = useSpecificRolesSelectorHandler({ selectedRoles, onSelectedRolesChange });

  const defaultRole = useMemo(() => selectedRoles.find(r => r.isDefault), [
    selectedRoles
  ]);

  const defaultLabel = defaultRole
    ? defaultRole.displayText
    : formatMessage({ id: 'selectTaskRolesDialog.noDefaultRole' });

  const tabCountLabel =
    selectedRoles.length > 1
      ? `${defaultLabel} +${selectedRoles.length - 1}`
      : defaultLabel;

  const selectedTabLabel = useMemo(
    () => (
      <span>
        <span className={classes.tabCount}>{tabCountLabel}</span>
        <br />
        <span className={classes.tabLabel}>
          <FormattedMessage id="selectTaskRolesDialog.selectedRoles" />
        </span>
      </span>
    ),
    [classes.tabCount, classes.tabLabel, tabCountLabel]
  );

  const allTabLabel = useMemo(
    () => (
      <span>
        <span className={classes.tabCount}>{allTabCount}</span>
        <br />
        <span className={classes.tabLabel}>
          <FormattedMessage id="selectTaskRolesDialog.allRoles" />
        </span>
      </span>
    ),
    [classes.tabCount, classes.tabLabel, allTabCount]
  );

  return (
    <>
      <SearchField
        value={searchTerm}
        onChange={handleSearchChange}
        placeholder={formatMessage({ id: 'selectTaskRolesDialog.search' })}
      />

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        className={classes.tabs}
        indicatorColor="primary"
      >
        <Tab
          value={TABS.SELECTED}
          className={classes.tab}
          label={selectedTabLabel}
        />
        <Tab value={TABS.ALL} className={classes.tab} label={allTabLabel} />
      </Tabs>

      {activeTab === TABS.ALL && (
        <>
          {isLoading ? (
            <>
              <Skeleton height={36} />
              <Skeleton height={36} />
              <Skeleton height={36} />
            </>
          ) : (
            <List disablePadding className={classes.list}>
              {rolesForAllTab.map(role => (
                <RoleSelectionRow
                  key={role.id}
                  roleUri={role.id}
                  displayText={role.displayText}
                  isChecked={selectedRoleIds.has(role.id)}
                  isDefault={role.id === defaultRoleUri}
                  onToggle={toggleRole}
                  onSetDefault={setDefault}
                />
              ))}
            </List>
          )}
          {hasMore && !isLoading && (
            <Typography className={classes.moreHint} component="span">
              <FormattedMessage id="selectTaskRolesDialog.searchToShowMore" />
            </Typography>
          )}
        </>
      )}

      {activeTab === TABS.SELECTED && (
        <List disablePadding className={classes.list}>
          {selectedRoles.length === 0 ? (
            <Typography className={classes.emptyText}>
              <FormattedMessage id="selectTaskRolesDialog.noSelectedRoles" />
            </Typography>
          ) : (
            selectedRoles.map(entry => (
              <RoleSelectionRow
                key={entry.roleUri}
                roleUri={entry.roleUri}
                displayText={entry.displayText}
                isChecked
                isDefault={entry.isDefault}
                onToggle={toggleRole}
                onSetDefault={setDefault}
              />
            ))
          )}
        </List>
      )}
    </>
  );
};

SpecificRolesSelector.propTypes = {
  selectedRoles: PropTypes.arrayOf(
    PropTypes.shape({
      roleUri: PropTypes.string.isRequired,
      isDefault: PropTypes.bool.isRequired,
      displayText: PropTypes.string.isRequired
    })
  ).isRequired,
  onSelectedRolesChange: PropTypes.func.isRequired
};

export default SpecificRolesSelector;
